import {
  generateShipmentNoEffect,
  getTrailerDetailsEffect,
  getTrailerRequestByIDEffect,
  getUserRelationEffect,
} from '@/redux/Savers/SaversEffects';
import { setAppLoader } from '@/redux/common/CommonActions';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { INITIAL_TRAILER_DETAILS, TAB_KEYS } from '../constants';

const useFieldsData = (
  companyId,
  requestID,
  formPickupManagement,
  formDeliveryManagement,
  setPickupFormValues,
  setDeliveryFormValues,
  isMultiple,
  nonMultiLabel,
  setCheckOptions,
  setPickupVendorId,
  setDeliveryVendorId,
  activeTabName,
) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    branchKey: null,
    createdBy: null,
    shipmentNumber: null,
    soNumberValue: null,
    poNumberValue: null,
    unitSize: null,
  });

  const getUserRelations = async () => {
    dispatch(setAppLoader(true));
    try {
      const { data } = await getUserRelationEffect();
      if (data.length > 0) {
        setFormData((prev) => ({
          ...prev,
          branchKey: data[0].branchKey,
        }));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const fetchTrailerRequestData = async (requestId) => {
    if (!requestId || requestId === '0') return;

    dispatch(setAppLoader(true));

    try {
      const { data: requestData } = await getTrailerRequestByIDEffect(requestId);

      if (!requestData.length || !requestData[0]?.trailerRequestData.length) return;

      const trailer = requestData[0].trailerRequestData[0];
      setFormData((prev) => ({
        ...prev,
        createdBy: trailer.createdBy,
      }));
      const {
        trailerID,
        trailerNumber,
        fullName,
        rscm,
        scs,
        unitSize,
        productTypeDesc,
        deliveryProductTypeDesc,
        deliveryProductType,
        productType,
        supplierNumber,
        locationID,
        branchDesc,
      } = trailer;
      const commonFormValues = {
        txtUserName: fullName,
        txtRSCM: rscm,
        txtSCS: scs,
        UnitSize: unitSize,
        selPartner: supplierNumber,
      };
      const pickupFormValues = {
        selTrailer: trailerNumber,
        Commodity: productTypeDesc,
        productType: productType,
        ...commonFormValues,
      };

      const deliveryFormValues = {
        Commodity: deliveryProductTypeDesc,
        productType: deliveryProductType,
        ...commonFormValues,
      };

      formPickupManagement.setFieldsValue(pickupFormValues);
      formDeliveryManagement.setFieldsValue(deliveryFormValues);
      setFormData((prev) => ({
        ...prev,
        unitSize: unitSize,
      }));
      const { data: trailerDetails } = await getTrailerDetailsEffect(trailerID);
      let extraValues = {};
      if (trailerDetails.length > 0) {
        const { yardName, unitRentalCompany, rollDoor, vendorId } = trailerDetails[0];
        if (!isMultiple) {
          setPickupVendorId(vendorId);
        } else {
          if (activeTabName === TAB_KEYS.PICKUP) {
            setPickupVendorId(vendorId);
          } else {
            setDeliveryVendorId(vendorId);
          }
        }
        extraValues = {
          selYardValue: yardName,
          PickupRentalCompany: unitRentalCompany && unitRentalCompany !== 'NULL' ? unitRentalCompany : null,
        };
        setCheckOptions((prev) => ({
          ...prev,
          rollDoor: rollDoor,
        }));
      }
      const orgLocations =
        trailerDetails.length > 0
          ? trailerDetails.map((trailer) => ({
              orgLocationId: locationID,
              orgShipLocation: branchDesc,
              orgSpotDate: moment(trailer.loadedDate ?? undefined),
              orgETD: null,
              orgSoftWeight: trailer.soft,
              orgSoftOks: trailer.softOks,
              orgMiscWeight: trailer.misc,
              orgMiscOks: trailer.miscOKs,
              orgBookWeight: trailer.book,
              orgBookOks: trailer.bookOks,
              orgFurn: trailer.furn,
              orgOLI: trailer.oli,
            }))
          : INITIAL_TRAILER_DETAILS.orgLocations.map((trailer) => ({
              ...trailer,
              orgLocationId: locationID,
              orgShipLocation: branchDesc,
            }));

      const getDestLocations = (shouldNullifyConsignee) =>
        trailerDetails.length > 0
          ? trailerDetails.map((trailer) => ({
              destLocationId: locationID,
              destConsignee: shouldNullifyConsignee ? null : branchDesc,
              destETA: null,
              destSoftWeight: trailer.soft,
              destSoftOks: trailer.softOks,
              destMiscWeight: trailer.misc,
              destMiscOks: trailer.miscOKs,
              destBookWeight: trailer.book,
              destBookOks: trailer.bookOks,
              destFurn: trailer.furn,
              destOLI: trailer.oli,
            }))
          : INITIAL_TRAILER_DETAILS.destLocations.map((trailer) => ({
              ...trailer,
              destLocationId: shouldNullifyConsignee ? null : locationID,
              destConsignee: shouldNullifyConsignee ? null : branchDesc,
            }));

      const finalPickupValues = {
        ...INITIAL_TRAILER_DETAILS,
        ...pickupFormValues,
        ...extraValues,
        orgLocations,
        destLocations: getDestLocations(true),
      };

      const finalDeliveryValues = {
        ...INITIAL_TRAILER_DETAILS,
        ...deliveryFormValues,
        ...extraValues,
        destLocations: getDestLocations(false),
      };

      if (isMultiple) {
        formPickupManagement.setFieldsValue(finalPickupValues);
        formDeliveryManagement.setFieldsValue(finalDeliveryValues);
        setPickupFormValues(finalPickupValues);
        setDeliveryFormValues(finalDeliveryValues);
      } else {
        const label = nonMultiLabel?.toLowerCase() || '';
        if (label.includes('pickup')) {
          formPickupManagement.setFieldsValue(finalPickupValues);
          setPickupFormValues(finalPickupValues);
        } else if (label.includes('delivery')) {
          formPickupManagement.setFieldsValue(finalDeliveryValues);
          setPickupFormValues(finalDeliveryValues);
        }
      }
    } catch (error) {
      console.error('Error fetching trailer request data:', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const generateShipmentNumber = async () => {
    try {
      dispatch(setAppLoader(true));

      const userInfo = JSON.parse(localStorage.getItem('userInfo'));

      if (userInfo) {
        const result = await generateShipmentNoEffect(userInfo.id);
        if (result?.data) {
          const shipmentNum = result.data.toString();

          setFormData((prev) => ({
            ...prev,
            shipmentNumber: shipmentNum,
            soNumberValue: `SO${shipmentNum}`,
            poNumberValue: `PO${shipmentNum}`,
          }));
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  useEffect(() => {
    getUserRelations();
    fetchTrailerRequestData(requestID);
    generateShipmentNumber();
  }, [companyId]);

  return {
    createdBy: formData.createdBy,
    shipmentNumber: formData.shipmentNumber,
    soNumberValue: formData.soNumberValue,
    poNumberValue: formData.poNumberValue,
    branchKey: formData.branchKey,
    unitSize: formData.unitSize,
  };
};

export default useFieldsData;
